import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Package, Search, Filter, Printer, ChevronLeft, ChevronRight } from 'lucide-react';
import bulletService, { BulletBooking } from '../services/bulletService';
import Button from '../components/ui/button/Button';
import PageMeta from '../components/common/PageMeta';

const BulletDashboard: React.FC = () => {
  // const [bookingStats, setBookingStats] = useState<BulletBookingStats | null>(null);
  const [recentBookings, setRecentBookings] = useState<BulletBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      const [bookingStatsData, allBookingsData] = await Promise.all([
        bulletService.getBookingStatistics(),
        bulletService.getAllBookings()
      ]);

      console.log('Dashboard data fetched:');
      console.log('Booking stats:', bookingStatsData);
      console.log('All bookings:', allBookingsData);
      console.log('Number of bookings:', allBookingsData?.length || 0);

      // setBookingStats(bookingStatsData);
      setRecentBookings(allBookingsData);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      const searchResults = await bulletService.searchBookings({
        customerName: searchTerm,
        trackingNumber: searchTerm,
        status: statusFilter || undefined
      });
      setRecentBookings(searchResults);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error searching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setStatusFilter('');
    setCurrentPage(1);
    fetchDashboardData();
  };

  // Pagination calculations
  const totalPages = Math.ceil(recentBookings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentBookings = recentBookings.slice(startIndex, endIndex);

  const goToPage = (page: number) => {
    setCurrentPage(page);
  };

  const goToPreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const formatStatus = (status: string) => {
    return status.replace('_', ' ').toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // const getStatusColor = (status: string) => {
  //   switch (status?.toUpperCase()) {
  //     case 'PENDING':
  //       return 'bg-yellow-100 text-yellow-800';
  //     case 'CONFIRMED':
  //       return 'bg-blue-100 text-blue-800';
  //     case 'PICKED_UP':
  //       return 'bg-purple-100 text-purple-800';
  //     case 'IN_TRANSIT':
  //       return 'bg-indigo-100 text-indigo-800';
  //     case 'OUT_FOR_DELIVERY':
  //       return 'bg-orange-100 text-orange-800';
  //     case 'DELIVERED':
  //       return 'bg-green-100 text-green-800';
  //     case 'CANCELLED':
  //       return 'bg-red-100 text-red-800';
  //     default:
  //       return 'bg-gray-100 text-gray-800';
  //   }
  // };

  // const getPaymentStatusColor = (paymentStatus: string) => {
  //   switch (paymentStatus?.toUpperCase()) {
  //     case 'PAID':
  //       return 'bg-green-100 text-green-800';
  //     case 'PENDING':
  //       return 'bg-yellow-100 text-yellow-800';
  //     case 'PROCESSING':
  //       return 'bg-blue-100 text-blue-800';
  //     case 'FAILED':
  //       return 'bg-red-100 text-red-800';
  //     default:
  //       return 'bg-gray-100 text-gray-800';
  //   }
  // };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      <PageMeta
        title="Bullet Dashboard - Bullet Delivery"
        description="Overview of your Bullet delivery service operations and key metrics."
        keywords="bullet dashboard, delivery metrics, logistics overview, booking statistics"
      />

      <div className="p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
          <p className="text-gray-600">Welcome to your Bullet delivery management dashboard</p>
        </div>

        {/* Bookings Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Package className="w-5 h-5" />
                <span>Recent Bookings</span>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  onClick={() => window.location.href = '/book-delivery'}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Book New Delivery
                </Button>
              </div>
            </CardTitle>

            {/* Search and Filter */}
            <div className="flex flex-col sm:flex-row gap-4 mt-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search by customer name or TMS tracking number..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Status</option>
                  <option value="PENDING">Pending</option>
                  <option value="CONFIRMED">Confirmed</option>
                  <option value="PICKED_UP">Picked Up</option>
                  <option value="IN_TRANSIT">In Transit</option>
                  <option value="OUT_FOR_DELIVERY">Out for Delivery</option>
                  <option value="DELIVERED">Delivered</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
                <Button onClick={handleSearch} variant="outline">
                  <Filter className="w-4 h-4 mr-2" />
                  Search
                </Button>
                {(searchTerm || statusFilter) && (
                  <Button onClick={clearSearch} variant="outline">
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {recentBookings.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2">Booking #</th>
                        <th className="text-left py-3 px-2">TMS Tracking #</th>
                        <th className="text-left py-3 px-2">Customer</th>
                        <th className="text-left py-3 px-2">From → To</th>
                        <th className="text-left py-3 px-2">Package</th>
                        <th className="text-left py-3 px-2">Pickup Date</th>
                        <th className="text-left py-3 px-2">Total</th>
                        <th className="text-left py-3 px-2">Created</th>
                        <th className="text-left py-3 px-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentBookings.map((booking) => (
                      <tr key={booking.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-2">
                          <div className="font-mono text-sm">{booking.bookingNumber}</div>
                        </td>
                        <td className="py-3 px-2">
                          <div className="font-mono text-sm text-blue-600">
                            {booking.tmsTrackingNumber || 'N/A'}
                          </div>
                        </td>
                        <td className="py-3 px-2">
                          <div className="font-medium">{booking.customerName}</div>
                          <div className="text-sm text-gray-600">{booking.customerEmail}</div>
                        </td>
                        <td className="py-3 px-2">
                          <div className="text-sm">
                            <div>{booking.pickupCity}, {booking.pickupCountry}</div>
                            <div className="text-gray-500">↓</div>
                            <div>{booking.dropoffCity}, {booking.dropoffCountry}</div>
                          </div>
                        </td>
                        <td className="py-3 px-2">
                          <div className="text-sm">
                            <div>{formatStatus(booking.packageType || '')}</div>
                            <div className="text-gray-500">{booking.weight} {booking.weightUnit}</div>
                          </div>
                        </td>
                        <td className="py-3 px-2">
                          <div className="text-sm">
                            {booking.pickupDate ? new Date(booking.pickupDate).toLocaleDateString('en-GB') : 'Not set'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {booking.timeSlot ? formatStatus(booking.timeSlot) : ''}
                          </div>
                        </td>
                        <td className="py-3 px-2">
                          <div className="font-medium">{booking.totalCost} {booking.totalCostCurrency}</div>
                        </td>
                        <td className="py-3 px-2">
                          <div className="text-xs text-gray-500">
                            {booking.createdAt ? new Date(booking.createdAt).toLocaleDateString('en-GB') : 'N/A'}
                          </div>
                        </td>
                        <td className="py-3 px-2">
                          {booking.tmsTrackingNumber ? (
                            <Button
                              onClick={() => window.open(`https://business-agent-stage.shypv.com/print-order?id=${booking.tmsTrackingNumber}`, '_blank')}
                              variant="outline"
                              className="flex items-center gap-1 text-xs py-1 px-2"
                            >
                              <Printer className="w-3 h-3" />
                              Print
                            </Button>
                          ) : (
                            <span className="text-xs text-gray-400">N/A</span>
                          )}
                        </td>
                      </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-6 px-2">
                    <div className="text-sm text-gray-600">
                      Showing {startIndex + 1} to {Math.min(endIndex, recentBookings.length)} of {recentBookings.length} bookings
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                        variant="outline"
                        className="flex items-center gap-1 px-3 py-2"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        Previous
                      </Button>

                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                          let pageNumber: number;
                          if (totalPages <= 5) {
                            pageNumber = i + 1;
                          } else if (currentPage <= 3) {
                            pageNumber = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNumber = totalPages - 4 + i;
                          } else {
                            pageNumber = currentPage - 2 + i;
                          }

                          return (
                            <button
                              key={pageNumber}
                              onClick={() => goToPage(pageNumber)}
                              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                currentPage === pageNumber
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              {pageNumber}
                            </button>
                          );
                        })}
                      </div>

                      <Button
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        variant="outline"
                        className="flex items-center gap-1 px-3 py-2"
                      >
                        Next
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium mb-2">No bookings found</h3>
                <p className="text-gray-400 mb-4">
                  {searchTerm || statusFilter ? 'No bookings match your search criteria' : 'Start by creating your first booking'}
                </p>
                <Button
                  onClick={() => window.location.href = '/book-delivery'}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Book Your First Delivery
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default BulletDashboard;
